/**
 * Toda imagen del sitio pasa por aca.
 *
 * El tipo obliga a lo que la regla de SEO pide y que en HTML suelto es opcional:
 * `alt`, `width` y `alto` son obligatorios. Sin `width`/`height` el navegador no
 * puede reservar el espacio y la pagina salta cuando bajan las fotos (CLS); sin
 * `alt` la ficha no se lee con lector de pantalla ni la indexa Google Imagenes.
 *
 * `prioridad` marca la unica imagen que se ve sin desplazar la pagina —el banner
 * del carrusel, la foto de la ficha—: esa se carga de inmediato porque es el
 * LCP. Todas las demas van en `lazy`.
 */
import type { ImagenProducto } from '@bandu/compartido';

import { Icono } from './Icono';

export interface ImagenProps {
  src: string;
  /** Que se ve en la foto. Vacio solo si la imagen es puramente decorativa. */
  alt: string;
  /** Medidas reales del archivo, para reservar el espacio antes de bajarlo. */
  ancho: number;
  alto: number;
  className?: string;
  /** true solo para la imagen que domina la primera pantalla (LCP). */
  prioridad?: boolean;
}

export function Imagen({ src, alt, ancho, alto, className, prioridad = false }: ImagenProps) {
  return (
    <img
      src={src}
      alt={alt}
      width={ancho}
      height={alto}
      loading={prioridad ? 'eager' : 'lazy'}
      decoding={prioridad ? 'sync' : 'async'}
      fetchPriority={prioridad ? 'high' : 'auto'}
      {...(className !== undefined ? { className } : {})}
    />
  );
}

export interface FotoProductoProps {
  imagen: ImagenProducto | null;
  /** Nombre del producto: es el respaldo del texto alternativo. */
  nombre: string;
  prioridad?: boolean;
}

/**
 * Foto de un producto, con su hueco cuando el producto todavia no tiene fotos.
 * El hueco es un icono y no una imagen rota: un catalogo de mil productos
 * siempre tiene algunos sin foto y eso no puede verse como un error.
 */
export function FotoProducto({ imagen, nombre, prioridad = false }: FotoProductoProps) {
  if (imagen === null) {
    return (
      <div className="sin-foto" role="img" aria-label={`Sin foto de ${nombre}`}>
        <Icono nombre="caja" />
      </div>
    );
  }

  return (
    <Imagen
      src={imagen.url}
      alt={imagen.textoAlternativo ?? nombre}
      ancho={imagen.ancho}
      alto={imagen.alto}
      prioridad={prioridad}
    />
  );
}
